import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Select } from 'antd';
import { ApiOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useDataProvider, useNotify, useRedirect } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { Field, FormSection, NetorcaForm, useServerErrors } from '../Common/Form';
import { EntityFormRoute } from '../Common/Integrations';
import MainCard from '../Common/MainCard/MainCard';
import DeleteButton from '../Common/DeleteButton';
import ChatCompletionModelForm from './LLMForms/ChatCompletionModelForm';
import OpenAIModelForm from './LLMForms/OpenAIModelForm';
import GenAIModelForm from './LLMForms/GenAIModelForm';
import './LLMModelForm.css';

const RESOURCE = 'llm_models';
const TITLE = 'LLM Model';

const PROVIDERS = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'openai_assistant', label: 'OpenAI Assistant' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'mistral', label: 'Mistral' },
    { value: 'azure', label: 'Azure' },
    { value: 'custom', label: 'Custom HTTP' },
    { value: 'xai', label: 'xAI' },
    { value: 'gemini', label: 'Google Gemini' },
    { value: 'cohere', label: 'Cohere' },
    { value: 'genai', label: 'Genai' },
];

/**
 * The API nests everything provider-specific under `extra_data`, so a rejection
 * comes back nested too — flattened here to the paths the form knows the fields
 * by, which is what puts the message on the field that caused it.
 */
const flattenErrors = (body: any) => {
    if (!body || typeof body !== 'object' || Array.isArray(body)) return body;

    const flat: Record<string, any> = {};
    Object.entries<any>(body).forEach(([key, value]) => {
        if (key === 'extra_data' && value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries<any>(value).forEach(([inner, messages]) => {
                flat[`extra_data.${inner}`] = messages;
            });
        } else {
            flat[key] = value;
        }
    });
    return flat;
};

/** the parameter rows are edited as a list and sent as an object */
const entriesToPayload = (values: any) => {
    const entries = values.extra_data?.request_payload_entries;
    if (!entries) return;

    const payload: Record<string, any> = {};
    for (const entry of entries) {
        if (!entry?.key) continue;
        let value = entry.value;
        if (value === 'true') value = true;
        else if (value === 'false') value = false;
        else if (value !== '' && !isNaN(Number(value))) value = Number(value);
        payload[entry.key] = value;
    }
    values.extra_data.request_payload = payload;
    delete values.extra_data.request_payload_entries;
};

const toFormValues = (record?: any) =>
    record
        ? {
              ...record,
              extra_data: {
                  ...record.extra_data,
                  request_payload_entries: record.extra_data?.request_payload
                      ? Object.entries(record.extra_data.request_payload).map(([key, value]) => ({
                            key,
                            value: String(value),
                        }))
                      : [],
              },
          }
        : {
              timeout: 60,
              input_price_per_1m_tokens: 0.0,
              output_price_per_1m_tokens: 0.0,
          };

const LLMModelForm = ({ record }: { record?: any }) => {
    const [form] = Form.useForm();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const { apply, fieldErrors, clear } = useServerErrors(form);
    const [saving, setSaving] = useState(false);

    const [testing, setTesting] = useState(false);
    /** what the last connection test said, until something is edited */
    const [tested, setTested] = useState<'success' | 'error' | null>(null);

    const editing = !!record;
    const provider = Form.useWatch('provider', form);

    const onFinish = (values: any) => {
        clear();
        setSaving(true);
        entriesToPayload(values);

        const request = editing
            ? dataProvider.update(RESOURCE, { id: record.id, data: values, previousData: { id: record.id } })
            : dataProvider.create(RESOURCE, { data: values });

        request
            .then(() => {
                notify(editing ? `${TITLE} updated` : `${TITLE} created`);
                redirect(`/${RESOURCE}`);
            })
            .catch((error: any) => {
                apply(
                    { ...error, body: flattenErrors(error?.body) },
                    editing ? `Could not update the ${TITLE}` : `Could not create the ${TITLE}`,
                );
                setSaving(false);
            });
    };

    /* the model is only worth saving if it answers — so it can be asked before
       saving, on whatever is in the form right now */
    const testConnection = () => {
        setTesting(true);
        form.validateFields()
            .then((values) => {
                entriesToPayload(values);
                const verb = record ? `test_connection/${record.id}/` : 'test_connection/';

                return dataProvider
                    .createAction(RESOURCE, { data: values, verb })
                    .then(() => {
                        notify('Connection test successful', 'success');
                        setTested('success');
                    })
                    .catch((error: any) => {
                        const message =
                            error.body?.error?.error?.message || error.body?.message || error.message || 'Unknown error';
                        const status = error.body?.status_code === 500 ? 400 : error.body?.status_code || error.status;
                        notify(`${message} (Status: ${status ?? 'unknown'})`, 'error');
                        setTested('error');
                    });
            })
            .catch(() => notify('Fill in the required fields before testing the connection', 'warning'))
            .finally(() => setTesting(false));
    };

    const connection =
        provider === 'openai_assistant' ? (
            <OpenAIModelForm />
        ) : provider === 'genai' ? (
            <GenAIModelForm />
        ) : (
            <ChatCompletionModelForm />
        );

    return (
        <NetorcaForm
            className="nf-form--sheet llm-form"
            form={form}
            name="llm_model"
            title={editing ? record.name || TITLE : 'New LLM model'}
            onBack={() => redirect(`/${RESOURCE}`)}
            backLabel="Back to LLM models"
            heroExtra={
                <Button
                    icon={
                        tested === 'success' ? (
                            <CheckCircleFilled />
                        ) : tested === 'error' ? (
                            <CloseCircleFilled />
                        ) : (
                            <ApiOutlined />
                        )
                    }
                    className={tested ? `llm-test llm-test--${tested}` : 'llm-test'}
                    loading={testing}
                    onClick={testConnection}
                >
                    {tested === 'success' ? 'Connection works' : tested === 'error' ? 'Connection failed' : 'Test connection'}
                </Button>
            }
            submitLabel={editing ? 'Save changes' : 'Create LLM model'}
            submitting={saving}
            onCancel={() => redirect(`/${RESOURCE}`)}
            onFinish={onFinish}
            serverErrors={fieldErrors}
            destructive={editing ? <DeleteButton title={TITLE} resource={RESOURCE} record={record} /> : undefined}
            initialValues={toFormValues(record)}
            /* anything edited makes the last test's verdict stale */
            onValuesChange={() => setTested(null)}
        >
            <FormSection id="model" title="LLM model" bare>
                <Field name="name" label="Name" span={3} rules={[{ required: true, message: 'Give the model a name' }]}>
                    <Input placeholder="GPT-5 Mini" />
                </Field>

                <Field
                    name="provider"
                    label="Provider"
                    span={3}
                    rules={[{ required: true, message: 'Choose the provider' }]}
                >
                    <Select placeholder="Select provider" options={PROVIDERS} showSearch={{ optionFilterProp: 'label' }} />
                </Field>

                <Field
                    name="model_name"
                    label="Model name"
                    span={3}
                    hint="As the provider names it."
                    rules={[{ required: true, message: 'Enter the model name' }]}
                >
                    <Input className="nf-mono" placeholder="gpt-4o, claude-sonnet-4, gemini-pro" />
                </Field>

                <Field
                    name="timeout"
                    label="Timeout"
                    span={3}
                    rules={[
                        { required: true, message: 'Set a timeout' },
                        { type: 'number', min: 1, max: 300, message: 'Between 1 and 300 seconds' },
                    ]}
                >
                    <InputNumber min={1} max={300} suffix="sec" style={{ width: '100%' }} />
                </Field>

                <div className="nf-rule" />

                <Field
                    className="llm-prompt"
                    name="prompt"
                    label="Prompt"
                    hint="The system prompt every call to this model carries."
                    rules={[{ required: true, message: 'Write the system prompt' }]}
                >
                    <Input.TextArea
                        autoSize={{ minRows: 6, maxRows: 30 }}
                        maxLength={10000}
                        showCount
                        placeholder="You are…"
                    />
                </Field>

                {provider && connection}

                <Field
                    name="input_price_per_1m_tokens"
                    label="Input price"
                    span={4}
                    hint="Per 1M input tokens."
                    rules={[{ type: 'number', min: 0, message: 'Must be a positive number' }]}
                >
                    <InputNumber placeholder="0.50" style={{ width: '100%' }} min={0} step={0.01} precision={6} />
                </Field>

                <Field
                    name="output_price_per_1m_tokens"
                    label="Output price"
                    span={4}
                    hint="Per 1M output tokens."
                    rules={[{ type: 'number', min: 0, message: 'Must be a positive number' }]}
                >
                    <InputNumber placeholder="1.50" style={{ width: '100%' }} min={0} step={0.01} precision={6} />
                </Field>
            </FormSection>
        </NetorcaForm>
    );
};

const LLMModelsCreate = (props: any) => (
    <EntityFormRoute
        id={props.id}
        resource={RESOURCE}
        basePath={`/${RESOURCE}`}
        render={(record) => (
            <MainCard className="llm-card">
                <LLMModelForm record={record} />
            </MainCard>
        )}
    />
);

export default LLMModelsCreate;
